function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { Component } from 'react';
import debounce from 'lodash.debounce';
import { ContextActions, ItemList, SearchInput, GLOBAL_SHORTCUTS } from '@deephaven/components';
import { vsFileCode, vsFiles, vsNewFile, vsPlay, vsTerminal } from '@deephaven/icons';
import { copyToClipboard, Pending } from '@deephaven/utils';
import Log from '@deephaven/log';
import CommandHistoryItem from "./CommandHistoryItem.js";
import CommandHistoryActions from "./CommandHistoryActions.js";
import ConsoleConstants from "../common/ConsoleConstants.js";
import "./CommandHistory.css";
import CommandHistoryViewportUpdater from "./CommandHistoryViewportUpdater.js";
import SHORTCUTS from "../ConsoleShortcuts.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var log = Log.module('CommandHistory');
class CommandHistory extends Component {
  static getCommandsFromViewport(items, offset, sortedRanges) {
    var commands = [];
    for (var i = 0; i < sortedRanges.length; i += 1) {
      var range = sortedRanges[i];
      for (var j = range[0]; j <= range[1]; j += 1) {
        if (j >= offset && j < offset + items.length) {
          var name = items[j - offset].name;
          commands.push(name);
        }
      }
    }
    return commands;
  }
  static getCommandsFromSnapshot(table, sortedRanges) {
    return _asyncToGenerator(function* () {
      var items = yield table.getSnapshot(sortedRanges);
      return [...items.values()].map(item => item.name);
    })();
  }
  constructor(props) {
    super(props);
    _defineProperty(this, "itemActions", void 0);
    _defineProperty(this, "historyActions", void 0);
    _defineProperty(this, "pending", void 0);
    _defineProperty(this, "searchInputRef", void 0);
    _defineProperty(this, "itemListRef", void 0);
    _defineProperty(this, "debouncedSearchChange", debounce(() => {
      this.setState(_ref => {
        var searchText = _ref.searchText;
        return {
          debouncedSearchText: searchText,
          // clear selected range, as old selection could be filtered from list
          selectedRanges: []
        };
      });
    }, CommandHistory.SET_SEARCH_DEBOUNCE_MS));
    this.copySelectedCommands = this.copySelectedCommands.bind(this);
    this.createNotebook = this.createNotebook.bind(this);
    this.sendToConsole = this.sendToConsole.bind(this);
    this.renderItem = this.renderItem.bind(this);
    this.runInConsole = this.runInConsole.bind(this);
    this.sendToNotebook = this.sendToNotebook.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleSelectionChange = this.handleSelectionChange.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleViewportUpdate = this.handleViewportUpdate.bind(this);
    this.itemActions = [{
      title: 'Copy Selection',
      description: 'Copy selected commands to clipboard',
      icon: vsFiles,
      shortcut: GLOBAL_SHORTCUTS.COPY,
      action: this.copySelectedCommands,
      group: ContextActions.groups.edit
    }, {
      title: 'Send to Console',
      description: 'Open selected commands in the console',
      icon: vsTerminal,
      shortcut: SHORTCUTS.COMMAND_HISTORY.SEND_TO_CONSOLE,
      action: this.sendToConsole,
      group: CommandHistory.menuGroups.send,
      order: 10
    }, {
      title: 'Run in Console',
      description: 'Run selected commands in the console',
      icon: vsPlay,
      shortcut: SHORTCUTS.COMMAND_HISTORY.RUN,
      action: this.runInConsole,
      group: CommandHistory.menuGroups.send,
      order: 10
    }, {
      title: 'Send to Active Notebook',
      description: 'Open selected commands in a notebook',
      icon: vsFileCode,
      shortcut: SHORTCUTS.COMMAND_HISTORY.SEND_TO_NOTEBOOK,
      action: this.sendToNotebook,
      group: CommandHistory.menuGroups.send,
      order: 20
    }, {
      title: 'Send to New Notebook',
      description: 'Open selected commands in a new notebook',
      icon: vsNewFile,
      action: this.createNotebook,
      group: CommandHistory.menuGroups.send,
      order: 30
    }];
    this.historyActions = [{
      title: 'Send to Console',
      icon: vsTerminal,
      selectionRequired: true,
      description: 'Open selected commands in the console',
      action: this.sendToConsole
    }, {
      title: 'Send to Notebook',
      icon: vsFileCode,
      selectionRequired: true,
      description: 'Open selected commands in a notebook',
      action: this.sendToNotebook
    }];
    this.pending = new Pending();
    this.searchInputRef = /*#__PURE__*/React.createRef();
    this.itemListRef = /*#__PURE__*/React.createRef();
    var table = props.table;
    this.state = {
      actions: [],
      historyActions: this.historyActions,
      top: 0,
      bottom: 0,
      itemCount: table.size,
      items: [],
      offset: 0,
      selectedRanges: [],
      searchText: '',
      debouncedSearchText: ''
    };
  }
  componentWillUnmount() {
    this.debouncedSearchChange.cancel();
    this.pending.cancel();
  }
  /**
   * Retrieves the selected commands as an array.
   * If they're not within the current viewport, will fetch them from the table
   * @returns Array of selected commands
   */
  getSelectedCommands() {
    var _this = this;
    return _asyncToGenerator(function* () {
      var _this$state = _this.state,
        items = _this$state.items,
        offset = _this$state.offset,
        selectedRanges = _this$state.selectedRanges;
      var ranges = selectedRanges.slice().sort((a, b) => a[0] - b[0]);
      if (ranges.length === 0) {
        return [];
      }
      if (ranges[0][0] >= offset && ranges[ranges.length - 1][1] < offset + items.length) {
        // All ranges are in the current viewport, just copy the data we've already got
        return CommandHistory.getCommandsFromViewport(items, offset, ranges);
      }
      var table = _this.props.table;
      return _this.pending.add(CommandHistory.getCommandsFromSnapshot(table, ranges));
    })();
  }

  /**
   * Retrieves the text of all the currently selected commands, joined by a new line char
   * @returns The commands joined by \n char
   */
  getSelectedCommandText() {
    return this.getSelectedCommands().then(commands => commands.join('\n'));
  }
  updateActions() {
    this.setState(state => {
      var selectedRanges = state.selectedRanges;
      var selectedRowCount = selectedRanges.reduce((count, range) => count + range[1] - range[0] + 1, 0);
      if (selectedRowCount > 0 && selectedRowCount < CommandHistory.MAX_SELECTION_COUNT) {
        log.debug('Updating actions', this.itemActions);
        return {
          actions: this.itemActions
        };
      }
      log.debug('Disabling actions', selectedRowCount);
      return {
        actions: []
      };
    });
  }
  copySelectedCommands() {
    this.getSelectedCommandText().then(copyToClipboard).catch(log.error);
  }
  createNotebook() {
    this.getSelectedCommandText().then(commandText => {
      var _this$props = this.props,
        language = _this$props.language,
        sendToNotebook = _this$props.sendToNotebook;
      sendToNotebook({
        value: commandText,
        language
      }, true);
    }).catch(log.error);
  }
  sendToNotebook() {
    this.getSelectedCommandText().then(commandText => {
      var _this$props2 = this.props,
        language = _this$props2.language,
        sendToNotebook = _this$props2.sendToNotebook;
      sendToNotebook({
        value: commandText,
        language
      });
    }).catch(log.error);
  }
  sendToConsole() {
    var sendToConsole = this.props.sendToConsole;
    this.getSelectedCommandText().then(sendToConsole).catch(log.error);
  }
  runInConsole() {
    this.getSelectedCommandText().then(commandText => {
      var sendToConsole = this.props.sendToConsole;
      sendToConsole(commandText, true, true);
    }).catch(log.error);
  }
  handleSelect(index) {
    var sendToConsole = this.props.sendToConsole;
    var _this$state2 = this.state,
      items = _this$state2.items,
      offset = _this$state2.offset;
    if (index < offset || index >= offset + items.length) {
      log.error('Invalid index!', index);
      return;
    }
    var name = items[index - offset].name;
    sendToConsole(name);
  }
  handleSelectionChange(selectedRanges) {
    this.setState({
      selectedRanges
    });
    this.updateActions();
  }
  handleViewportChange(top, bottom) {
    this.setState({
      top,
      bottom
    });
  }
  handleSearchChange(e) {
    this.setState({
      searchText: e.target.value
    });
    this.debouncedSearchChange();
  }
  handleViewportUpdate(_ref2) {
    var items = _ref2.items,
      offset = _ref2.offset;
    var table = this.props.table;
    var itemCount = table.size;
    this.setState({
      items,
      itemCount,
      offset
    });
  }
  renderItem(_ref3) {
    var item = _ref3.item,
      itemIndex = _ref3.itemIndex,
      isSelected = _ref3.isSelected;
    var _this$props3 = this.props,
      language = _this$props3.language,
      commandHistoryStorage = _this$props3.commandHistoryStorage;
    return /*#__PURE__*/_jsx(CommandHistoryItem, {
      isSelected: isSelected,
      item: item,
      language: language,
      commandHistoryStorage: commandHistoryStorage
    });
  }
  focus() {
    if (this.searchInputRef.current) {
      this.searchInputRef.current.focus();
    }
  }
  restoreScrollPosition() {
    var _this$itemListRef$cur;
    (_this$itemListRef$cur = this.itemListRef.current) === null || _this$itemListRef$cur === void 0 || _this$itemListRef$cur.restoreScrollPosition();
  }
  render() {
    var _ConsoleConstants$LAN;
    var _this$props4 = this.props,
      language = _this$props4.language,
      table = _this$props4.table;
    var _this$state3 = this.state,
      actions = _this$state3.actions,
      historyActions = _this$state3.historyActions,
      searchText = _this$state3.searchText,
      debouncedSearchText = _this$state3.debouncedSearchText,
      top = _this$state3.top,
      bottom = _this$state3.bottom,
      items = _this$state3.items,
      itemCount = _this$state3.itemCount,
      offset = _this$state3.offset,
      selectedRanges = _this$state3.selectedRanges;
    var languageDisplayString = (_ConsoleConstants$LAN = ConsoleConstants.LANGUAGE_MAP.get(language)) !== null && _ConsoleConstants$LAN !== void 0 ? _ConsoleConstants$LAN : '';
    return /*#__PURE__*/_jsxs("div", {
      className: "command-history",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "command-history-search-bar",
        children: [/*#__PURE__*/_jsx(SearchInput, {
          ref: this.searchInputRef,
          value: searchText,
          placeholder: "".concat(languageDisplayString, " Command History"),
          onChange: this.handleSearchChange
        }), /*#__PURE__*/_jsx(CommandHistoryActions, {
          actions: historyActions,
          hasSelection: selectedRanges.length > 0
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "command-history-list",
        children: [/*#__PURE__*/_jsx(ItemList, {
          ref: this.itemListRef,
          itemCount: itemCount,
          items: items,
          offset: offset,
          selectedRanges: selectedRanges,
          onSelect: this.handleSelect,
          onSelectionChange: this.handleSelectionChange,
          onViewportChange: this.handleViewportChange,
          renderItem: this.renderItem,
          rowHeight: CommandHistory.ITEM_HEIGHT,
          isDoubleClickSelect: true,
          isMultiSelect: true,
          isStickyBottom: true
        }), /*#__PURE__*/_jsx(CommandHistoryViewportUpdater, {
          table: table,
          top: top,
          bottom: bottom,
          search: debouncedSearchText,
          onUpdate: this.handleViewportUpdate
        }), /*#__PURE__*/_jsx(ContextActions, {
          actions: actions
        })]
      })]
    });
  }
}
_defineProperty(CommandHistory, "ITEM_HEIGHT", 29);
_defineProperty(CommandHistory, "MAX_SELECTION_COUNT", 10000);
_defineProperty(CommandHistory, "SET_SEARCH_DEBOUNCE_MS", 150);
_defineProperty(CommandHistory, "menuGroups", {
  send: ContextActions.groups.medium + 100
});
export default CommandHistory;
//# sourceMappingURL=CommandHistory.js.map